import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import { DraftContextHeader } from "@/components/draft/DraftContextHeader";
import { DraftStepFooter } from "@/components/drafting/DraftStepFooter";
import { ErrorAlert } from "@/components/common/ErrorAlert";
import { GlassCard } from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import {
  computeReviewMetrics,
  isOpenFinding,
  severityBadgeClass,
  severityLabel,
} from "@/lib/draft-review-utils";
import { fetchDraftFindings, generateDraftReview, updateDraftFinding } from "@/lib/api/drafts";
import { useDraftMatter } from "@/hooks/useDraftMatters";
import type { DraftReviewFinding, DraftReviewMetrics } from "@/types/drafting";

export function DraftReviewPage() {
  const { id } = useParams<{ id: string }>();
  const { matter, isLoading, error } = useDraftMatter(id, { syncStepStatus: "review" });
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canGenerate = hasPermission("draft", "regenerate");

  const [findings, setFindings] = useState<DraftReviewFinding[]>([]);
  const [findingsError, setFindingsError] = useState<string | null>(null);
  const [isLoadingFindings, setIsLoadingFindings] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [metrics, setMetrics] = useState<DraftReviewMetrics | null>(null);
  const [groundingSummary, setGroundingSummary] = useState<string | null>(null);

  const loadFindings = useCallback(async () => {
    if (!id) return;
    setIsLoadingFindings(true);
    setFindingsError(null);
    try {
      const rows = await fetchDraftFindings(id);
      setFindings(rows);
      setMetrics((prev) => {
        if (prev) {
          return computeReviewMetrics(rows, prev.playbook_conformance_percent);
        }
        return rows.length ? computeReviewMetrics(rows) : null;
      });
    } catch (err) {
      setFindingsError(err instanceof Error ? err.message : "Failed to load findings");
    } finally {
      setIsLoadingFindings(false);
    }
  }, [id]);

  useEffect(() => {
    void loadFindings();
  }, [loadFindings]);

  const displayMetrics = useMemo(
    () => metrics ?? computeReviewMetrics(findings),
    [findings, metrics]
  );

  const groundingText = useMemo(() => {
    if (groundingSummary) return groundingSummary;
    const jurisdiction = matter?.jurisdiction?.trim();
    if (jurisdiction) {
      return `Runs automated quality and policy checks grounded in ${jurisdiction}.`;
    }
    return "Runs automated quality and policy checks against the current draft.";
  }, [groundingSummary, matter?.jurisdiction]);

  async function handleGenerateReview() {
    if (!id || isGenerating) return;
    setIsGenerating(true);
    setFindingsError(null);
    try {
      const result = await generateDraftReview(id);
      setFindings(result.findings);
      setMetrics(result.metrics);
      setGroundingSummary(result.grounding_summary);
      showToast("Draft review complete.", "success");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to generate review";
      setFindingsError(message);
      showToast(message, "error");
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleResolve(finding: DraftReviewFinding) {
    if (!id) return;
    try {
      const updated = await updateDraftFinding(id, finding.id, { status: "resolved-manually" });
      setFindings((prev) => {
        const next = prev.map((item) => (item.id === finding.id ? updated : item));
        setMetrics((current) =>
          computeReviewMetrics(next, current?.playbook_conformance_percent)
        );
        return next;
      });
    } catch (err) {
      setFindingsError(err instanceof Error ? err.message : "Failed to update finding");
    }
  }

  if (isLoading) {
    return <p className="p-8 text-sm text-[var(--lm-muted)]">Loading review...</p>;
  }

  if (error || !matter) {
    return (
      <div className="p-8">
        <ErrorAlert message={error ?? "Draft matter not found"} />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="min-w-0 shrink-0">
        <DraftContextHeader matter={matter} currentStep={4} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <main className="p-4 md:p-6">
          <div className="mx-auto max-w-5xl space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
                Cross-Agent Review &amp; Compliance
              </h2>
              <p className="mt-1 text-sm text-[var(--lm-muted)] dark:text-slate-400">{groundingText}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {canGenerate ? (
                <Button
                  type="button"
                  variant="default"
                  className="gap-2"
                  disabled={isGenerating}
                  onClick={() => void handleGenerateReview()}
                >
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {isGenerating ? "Generating review…" : "Generate review"}
                </Button>
              ) : null}
            </div>
          </div>

          {findingsError ? <ErrorAlert message={findingsError} /> : null}

          <GlassCard className="border-[var(--lm-border-card)] bg-white dark:border-glass-border dark:bg-slate-950/45">
            <div className="grid gap-4 sm:grid-cols-3">
              <MetricTile
                label="Playbook conformance"
                value={`${displayMetrics.playbook_conformance_percent}%`}
              />
              <MetricTile label="Active issues" value={String(displayMetrics.active_issues)} />
              <MetricTile label="Resolved items" value={String(displayMetrics.resolved_items)} />
            </div>
          </GlassCard>

          <GlassCard className="border-[var(--lm-border-card)] bg-white dark:border-glass-border dark:bg-slate-950/45">
            <h3 className="mb-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--lm-muted)] dark:text-slate-500">
              Diagnostic findings
            </h3>

            {isLoadingFindings ? (
              <div className="flex items-center gap-2 text-sm text-[var(--lm-muted)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading findings...
              </div>
            ) : null}

            {!isLoadingFindings && findings.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[var(--lm-border-subtle)] px-4 py-8 text-center dark:border-slate-700">
                <p className="text-sm text-[var(--lm-muted)] dark:text-slate-400">
                  No review findings yet. Generate a review to run automated quality and policy checks
                  on the current draft.
                </p>
                {canGenerate ? (
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="mt-4 gap-2"
                    disabled={isGenerating}
                    onClick={() => void handleGenerateReview()}
                  >
                    <Sparkles className="h-4 w-4" />
                    Generate review
                  </Button>
                ) : null}
              </div>
            ) : null}

            <div className="space-y-3">
              {findings.map((finding) => (
                <div
                  key={finding.id}
                  className="rounded-xl border border-[var(--lm-border-subtle)] p-4 dark:border-slate-700"
                >
                  <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
                    <h4 className="text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
                      {finding.title}
                    </h4>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${severityBadgeClass(finding.severity)}`}
                    >
                      {severityLabel(finding.severity)}
                    </span>
                  </div>
                  <p className="mb-3 text-sm leading-relaxed text-[var(--lm-muted)] dark:text-slate-300">
                    {finding.explanation}
                  </p>
                  {finding.proposed_revision ? (
                    <p className="mb-3 rounded-lg bg-[var(--surface-sunken)] p-3 text-sm italic dark:bg-slate-900/50">
                      {finding.proposed_revision}
                    </p>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={!isOpenFinding(finding)}
                      onClick={() => void handleResolve(finding)}
                      className={
                        isOpenFinding(finding)
                          ? undefined
                          : "border-green-500/50 bg-green-500/15 text-green-700 disabled:opacity-100 dark:border-green-400/40 dark:bg-green-500/20 dark:text-green-200"
                      }
                    >
                      {isOpenFinding(finding) ? "Mark resolved" : "Resolved"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
          </div>
        </main>
      </div>

      <div className="shrink-0">
        <DraftStepFooter matterId={matter.id} currentStep={4} />
      </div>
    </div>
  );
}

function MetricTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center sm:text-left">
      <p className="text-2xl font-semibold text-[var(--lm-foreground-strong)] dark:text-white">{value}</p>
      <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-500">
        {label}
      </p>
    </div>
  );
}
